import { useEffect, useState } from 'react';
import { Alert, Button, CircularProgress, Stack, Typography } from '@mui/material';
import { Add } from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import type { TeamsApi, Team } from 'api-client';
import TeamDirectory from './TeamDirectory';
import PendingInvitations from './PendingInvitations';

export default function TeamList({ apiClient }: { apiClient: TeamsApi }) {
  const [teams, setTeams] = useState<Team[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<boolean>(false);
  const [retry, setRetry] = useState<number>(0);
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(false);
    const load = async () => {
      const all: Team[] = [];
      let page = 1;
      while (!cancelled) {
        const result = await apiClient.teamsList({ page });
        all.push(...(result.results || []));
        if (!result.next) break;
        page += 1;
      }
      if (!cancelled) setTeams(all);
    };
    load()
      .catch(() => {
        if (!cancelled) setError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [apiClient, retry]);
  return (
    <Stack spacing={3}>
      <Stack direction="row" useFlexGap flexWrap="wrap" gap={2} justifyContent="space-between" alignItems="center">
        <Typography component="h1" variant="h4">
          Teams
        </Typography>
        <Button variant="contained" startIcon={<Add />} component={RouterLink} to="/teams/new">
          New Team
        </Button>
      </Stack>
      {loading ? (
        <CircularProgress aria-label="Loading teams" />
      ) : error ? (
        <Alert
          severity="error"
          action={
            <Button color="inherit" onClick={() => setRetry((value) => value + 1)}>
              Retry teams
            </Button>
          }
        >
          Could not load teams.
        </Alert>
      ) : (
        <TeamDirectory teams={teams} />
      )}
      <PendingInvitations apiClient={apiClient} />
    </Stack>
  );
}
